import { CommonModule } from '@angular/common';
import { Component, DoCheck, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AttachmentPreviewComponent } from '../../shared/attachment-preview/attachment-preview.component';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { statusSeverity } from '../warehousing-common';
import { itemIdentityLabel, itemSizeLabel, WarehousingItemDimensions } from '../warehousing-item-display';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingBulkAction, WarehousingBulkBlockingDocument, WarehousingBulkPreview, WarehousingMasterData, WarehousingShipmentWork,
  WarehousingShipmentWorkAttachment, WarehousingShipmentWorkLine, WarehousingShipmentWorkRow, WarehousingWorkHistory,
  WarehousingInboundNotice, WarehousingInboundNoticeException, WarehousingReturnRequest,
  WarehousingShipmentInboundRequest,
  WarehousingReturnReceiveLine } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { warehouseWorkMethodLabel } from './warehousing-work-method';

@Component({
  selector: 'app-warehousing-shipment-work',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, ButtonModule, DialogModule,
    ProgressBarModule, SelectModule, TableModule, TagModule, ToastModule,
    IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputTextModule,
    WarehousingPageHeaderComponent, AttachmentPreviewComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-shipment-work.component.html',
  styleUrl: './warehousing-shipment-work.component.css'
})
export class WarehousingShipmentWorkComponent implements OnInit, DoCheck {
  private warehousingService = inject(WarehousingService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private permissions = inject(PermissionService);

  isLoading = false;
  work?: WarehousingShipmentWork;
  selectedKeys = new Set<string>();
  bulkAction: WarehousingBulkAction = 'RESERVE';
  bulkPreview?: WarehousingBulkPreview;
  bulkBlockers: Array<{ reason: string; count: number; blockingDocument?: WarehousingBulkBlockingDocument | null }> = [];
  private bulkIdempotencyKey = '';
  private bulkCorrelationId = '';
  bulkDialogVisible = false;
  bulkReason = '';
  selectedReceiptZoneId?: number;
  zones: WarehousingMasterData[] = [];
  conditions: WarehousingMasterData[] = [];
  warehouses: WarehousingMasterData[] = [];
  isBulkBusy = false;
  historyDialogVisible = false;
  historyTitle = '';
  historyRows: WarehousingWorkHistory[] = [];
  methodLabel = warehouseWorkMethodLabel;
  private activeCompanyId = 0;
  warehouseId?: number;
  workDirection: 'INBOUND' | 'OUTBOUND' = 'OUTBOUND';
  workType: 'SHIPMENT' | 'DELIVERY' = 'SHIPMENT';
  workId = 0;
  showCompleted = false;
  notices: WarehousingInboundNotice[] = [];
  returnRequests: WarehousingReturnRequest[] = [];
  shipmentInbounds: WarehousingShipmentInboundRequest[] = [];
  returnDraft: Record<number, { quantity: number; zoneId?: number; conditionId?: number;
    disposition: 'AVAILABLE' | 'HOLD'; unitCodes: string; exceptionReason: string }> = {};
  returnBusy = false;
  selectedNoticeId?: number;
  countDraft: Record<string, number> = {};
  codeDraft: Record<string, string> = {};
  noticeZoneId?: number;
  noScanReason = '';
  noticeBusy = false;
  resolutionReason = '';
  resolvingException?: WarehousingInboundNoticeException;

  canReceiveNotice(): boolean { return this.permissions.hasPermission(app_permissions.warehousing.transfer.receive); }
  canViewNotice(): boolean { return this.permissions.hasPermission(app_permissions.warehousing.transfer.view); }
  canResolveNotice(): boolean { return this.permissions.hasPermission(app_permissions.warehousing.stock.adjust); }
  canViewReturn(): boolean { return this.permissions.hasPermission(app_permissions.warehousing.stock_entry.view); }
  canReceiveReturn(): boolean { return this.permissions.hasPermission(app_permissions.warehousing.stock_entry.create); }
  selectedNotice(): WarehousingInboundNotice | undefined {
    return this.notices.find(notice => notice.id === this.selectedNoticeId);
  }

  warehouseLabel(warehouseId?: number): string {
    if (!warehouseId) return 'Warehouse not mapped';
    const warehouse = this.warehouses.find(row => row.id === warehouseId);
    if (warehouse) return warehouse.code ? `${warehouse.name} (${warehouse.code})` : warehouse.name;
    return this.work?.rows.find(row => row.destinationWarehouseId === warehouseId)?.destinationWarehouseLabel ||
      `Warehouse ID ${warehouseId}`;
  }

  conditionLabel(conditionId?: number): string {
    if (!conditionId) return 'Condition not set';
    return this.conditions.find(row => row.id === conditionId)?.name || `Condition ID ${conditionId}`;
  }

  itemLabel(item: { itemNumber?: string; itemName?: string; dimensions?: WarehousingItemDimensions;
    sqm?: number; cbm?: number }, fallback = 'Item mapping required'): string {
    const display = { ...item, ...item.dimensions };
    const identity = itemIdentityLabel(display, fallback);
    return item.itemNumber || item.itemName ? `${identity}${itemSizeLabel(display) ? '' : ' | Size not recorded'}` : identity;
  }

  noticeWarehouseLabel(warehouseId: number): string {
    return this.warehouseLabel(warehouseId);
  }

  noticeGroupLabel(groupKey: string): string {
    const group = this.selectedNotice()?.groups.find(row => row.key === groupKey);
    return group ? `${this.itemLabel(group)} · Lot ${group.lotId || '—'} · Source ${this.warehouseLabel(group.sourceWarehouseId)}` : groupKey;
  }

  ngOnInit(): void {
    this.activeCompanyId = this.warehousingService.getCompanyId();
    this.workType = this.route.snapshot.paramMap.get('type') === 'DELIVERY' ? 'DELIVERY' : 'SHIPMENT';
    this.workId = Number(this.route.snapshot.paramMap.get('id'));
    this.warehouseId = Number(this.route.snapshot.queryParamMap.get('warehouseId')) || undefined;
    this.workDirection = this.route.snapshot.queryParamMap.get('direction') === 'INBOUND' ? 'INBOUND' : 'OUTBOUND';
    this.warehousingService.getMasterData('ZONE').subscribe({
      next: response => this.zones = response.data || [],
      error: () => this.zones = []
    });
    this.warehousingService.getMasterData('ITEM_CONDITION').subscribe({
      next: response => this.conditions = response.data || [],
      error: () => this.conditions = []
    });
    this.warehousingService.getMasterData('WAREHOUSE').subscribe({
      next: response => this.warehouses = response.data || [],
      error: () => this.warehouses = []
    });
    if (this.workId) this.search(false);
  }

  ngDoCheck(): void {
    const companyId = this.warehousingService.getCompanyId();
    if (companyId === this.activeCompanyId) return;
    this.activeCompanyId = companyId;
    this.router.navigate(this.directoryLink());
  }

  search(persist = true): void {
    if (!this.workId) return;
    this.isLoading = true;
    this.warehousingService.getShipmentWorkById(this.workType, this.workId).subscribe({
      next: response => {
        const work = response.data;
        this.work = work ? {
          ...work,
          rows: work.rows || [],
          attachments: work.attachments || []
        } : undefined;
        this.selectedKeys.clear();
        if (work?.shipmentId && this.canViewNotice() && (this.workDirection === 'INBOUND' || !this.warehouseId))
          this.loadNotices(work.shipmentId);
        else this.notices = [];
        if (work?.shipmentId && this.canViewReturn() && (this.workDirection === 'INBOUND' || !this.warehouseId))
          this.loadReturnRequests(work.shipmentId);
        else this.returnRequests = [];
        if (work?.shipmentId && this.canViewReturn() && (this.workDirection === 'INBOUND' || !this.warehouseId))
          this.loadShipmentInbounds(work.shipmentId);
        else this.shipmentInbounds = [];
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.work = undefined;
        this.selectedKeys.clear();
        this.messageService.add({
          severity: 'error',
          summary: 'Reference Not Found',
          detail: error?.error?.message || 'Unable to load warehouse work for this shipment or delivery.'
        });
      }
    });
  }

  workQueryParams(): Record<string, string | null> {
    return { fromShipmentWork: '1', companyId: String(this.warehousingService.getCompanyId()), shipmentNumber: this.work?.shipmentNumber || null,
      deliveryNumber: this.work?.selectedDeliveryNumber || null, workType: this.workType,
      workId: String(this.workId), warehouseId: this.warehouseId ? String(this.warehouseId) : null,
      direction: this.workDirection };
  }

  directoryLink(): string[] {
    return ['/warehousing/shipment-work', this.workDirection.toLowerCase()];
  }

  directoryParams(): Record<string, string | null> {
    return { warehouseId: this.warehouseId ? String(this.warehouseId) : null, tab: this.workType };
  }

  selectWorkDirection(direction: 'INBOUND' | 'OUTBOUND'): void {
    if (direction === this.workDirection) return;
    this.workDirection = direction;
    this.selectedKeys.clear();
    this.showCompleted = false;
    if (direction === 'INBOUND' && this.work?.shipmentId && this.canViewNotice())
      this.loadNotices(this.work.shipmentId);
    else this.notices = [];
    if (direction === 'INBOUND' && this.work?.shipmentId && this.canViewReturn())
      this.loadReturnRequests(this.work.shipmentId);
    else this.returnRequests = [];
    if (direction === 'INBOUND' && this.work?.shipmentId && this.canViewReturn())
      this.loadShipmentInbounds(this.work.shipmentId);
    else this.shipmentInbounds = [];
    this.router.navigate([], { relativeTo: this.route, queryParams: { direction },
      queryParamsHandling: 'merge', replaceUrl: true });
  }

  noticeZones(): WarehousingMasterData[] {
    const destination = this.selectedNotice()?.destinationWarehouseId;
    return this.zones.filter(zone => zone.parentId === destination);
  }

  returnZones(request: WarehousingReturnRequest): WarehousingMasterData[] {
    return this.zones.filter(zone => zone.parentId === request.warehouseId);
  }

  private loadShipmentInbounds(shipmentId: number): void {
    this.warehousingService.getShipmentInbounds(shipmentId, this.warehouseId).subscribe({
      next: response => this.shipmentInbounds = (response.data || []).filter(request =>
        this.workType === 'SHIPMENT' || request.lines.some(line => line.deliveryId === this.workId)),
      error: error => this.messageService.add({ severity: 'error', summary: 'Shipment inbound unavailable',
        detail: error?.error?.message || 'Unable to load inbound warehouse requests.' })
    });
  }

  private loadReturnRequests(shipmentId: number): void {
    this.warehousingService.getReturnRequests(shipmentId, this.warehouseId).subscribe({
      next: response => {
        this.returnRequests = (response.data || []).filter(row =>
          this.workType === 'SHIPMENT' || row.deliveryId === this.workId);
        for (const request of this.returnRequests) for (const line of request.lines) {
          this.returnDraft[line.id] ||= {
            quantity: line.received ?? line.expected, zoneId: line.zoneId,
            conditionId: line.conditionId, disposition: line.disposition === 'HOLD' ? 'HOLD' : 'AVAILABLE',
            unitCodes: '', exceptionReason: line.exceptionReason || ''
          };
        }
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Returns unavailable',
        detail: error?.error?.message || 'Unable to load warehouse return requests.' })
    });
  }

  receiveReturn(request: WarehousingReturnRequest): void {
    if (this.returnBusy || request.status !== 'READY') return;
    const lines: WarehousingReturnReceiveLine[] = request.lines.map(line => {
      const draft = this.returnDraft[line.id];
      return { lineId: line.id, quantity: Number(draft?.quantity), zoneId: draft?.zoneId,
        conditionId: draft?.conditionId, disposition: draft?.disposition || 'AVAILABLE',
        unitCodes: (draft?.unitCodes || '').split(/[\n,]+/).map(code => code.trim()).filter(Boolean),
        exceptionReason: draft?.exceptionReason?.trim() || undefined };
    });
    if (lines.some(line => !Number.isFinite(line.quantity) || line.quantity < 0
        || (line.quantity > 0 && (!line.zoneId || !line.conditionId)))) {
      this.messageService.add({ severity: 'warn', summary: 'Check quantities, zones, and conditions' });
      return;
    }
    this.returnBusy = true;
    this.warehousingService.receiveReturn(request.id, lines).subscribe({
      next: () => { this.returnBusy = false; this.messageService.add({ severity: 'success',
        summary: 'Returned cargo received' }); this.search(false); },
      error: error => { this.returnBusy = false; this.messageService.add({ severity: 'error',
        summary: 'Return receipt failed', detail: error?.error?.message || 'Review the return and try again.' }); }
    });
  }

  selectNotice(id?: number): void {
    this.selectedNoticeId = id;
    this.noScanReason = '';
    this.noticeZoneId = undefined;
    const notice = this.selectedNotice();
    this.countDraft = {};
    this.codeDraft = {};
    for (const group of notice?.groups || []) {
      if (group.counted != null) this.countDraft[group.key] = group.counted;
      const count = notice?.counts.find(row => row.groupKey === group.key);
      this.codeDraft[group.key] = (count?.unitCodes || []).join('\n');
    }
  }

  private loadNotices(shipmentId: number): void {
    this.warehousingService.getInboundNotices(shipmentId, this.warehouseId).subscribe({
      next: response => {
        this.notices = response.data || [];
        this.selectNotice(this.notices.some(row => row.id === this.selectedNoticeId)
          ? this.selectedNoticeId! : this.notices[0]?.id);
      },
      error: error => {
        this.notices = [];
        this.messageService.add({ severity: 'error', summary: 'Inbound notices unavailable',
          detail: error?.error?.message || 'Refresh the work detail to try again.' });
      }
    });
  }

  private noticeAction(action: ReturnType<WarehousingService['markInboundArrived']>, success: string): void {
    this.noticeBusy = true;
    action.subscribe({
      next: response => {
        this.noticeBusy = false;
        const notice = response.data;
        this.notices = this.notices.map(row => row.id === notice.id ? notice : row);
        this.selectNotice(notice.id);
        this.messageService.add({ severity: 'success', summary: success });
        this.search(false);
      },
      error: error => {
        this.noticeBusy = false;
        this.messageService.add({ severity: 'error', summary: 'Notice action failed',
          detail: error?.error?.message || 'Refresh the notice and try again.' });
      }
    });
  }

  markNoticeArrived(): void {
    const notice = this.selectedNotice();
    if (notice) this.noticeAction(this.warehousingService.markInboundArrived(notice.id), 'Arrival recorded');
  }

  saveNoticeCounts(): void {
    const notice = this.selectedNotice();
    if (!notice) return;
    const counts = notice.groups.map(group => ({ groupKey: group.key,
      quantity: Number(this.countDraft[group.key]),
      unitCodes: (this.codeDraft[group.key] || '').split(/[\n,]+/).map(code => code.trim()).filter(Boolean) }));
    if (counts.some(row => !Number.isFinite(row.quantity) || row.quantity < 0)) {
      this.messageService.add({ severity: 'warn', summary: 'Enter a valid count for every item' });
      return;
    }
    this.noticeAction(this.warehousingService.saveInboundCounts(notice.id, counts), 'Physical count saved');
  }

  confirmNotice(): void {
    const notice = this.selectedNotice();
    if (!notice || !this.noticeZoneId || !this.noScanReason.trim()) return;
    this.noticeAction(this.warehousingService.confirmInboundNotice(notice.id, this.noticeZoneId,
      this.noScanReason.trim(), crypto.randomUUID()), 'Counted transfers received');
  }

  resolveNoticeException(exception: WarehousingInboundNoticeException): void {
    this.resolvingException = exception;
    this.resolutionReason = '';
  }

  submitNoticeResolution(): void {
    const notice = this.selectedNotice();
    const exception = this.resolvingException;
    if (!notice || !exception || !this.resolutionReason.trim()) return;
    this.noticeAction(this.warehousingService.resolveInboundException(notice.id, exception.id,
      this.resolutionReason.trim(), this.noticeZoneId), 'Exception resolved');
    this.resolvingException = undefined;
  }

  verifyNotice(): void {
    const notice = this.selectedNotice();
    if (notice) this.noticeAction(this.warehousingService.verifyInboundNotice(notice.id), 'Notice verified');
  }

  visibleRows(): WarehousingShipmentWorkRow[] {
    const rows = this.work?.rows || [];
    const relevant = this.warehouseId ? rows.filter(row => {
      if (this.workDirection === 'INBOUND') return row.documentType === 'TRANSFER'
        && row.destinationWarehouseId === this.warehouseId
        && (this.isInbound(row) || row.status === 'RECEIVED');
      return row.sourceWarehouseId === this.warehouseId;
    }) : rows;
    return relevant.filter(row => this.showCompleted || this.isOpen(row))
      .sort((a, b) => Number(this.isOpen(b)) - Number(this.isOpen(a)) ||
        (a.routeLoadSequence || 0) - (b.routeLoadSequence || 0));
  }

  isOpen(row: WarehousingShipmentWorkRow): boolean {
    if (this.workDirection === 'INBOUND' && this.warehouseId) return row.documentType === 'TRANSFER'
      && row.destinationWarehouseId === this.warehouseId && this.isInbound(row);
    if (this.workDirection === 'OUTBOUND' && this.warehouseId && row.documentType === 'TRANSFER')
      return !['IN_TRANSIT', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'].includes(row.status || '');
    return !['RECEIVED', 'DISPATCHED', 'CANCELLED'].includes(row.status || '');
  }

  private isInbound(row: WarehousingShipmentWorkRow): boolean {
    return ['IN_TRANSIT', 'PARTIALLY_RECEIVED'].includes(row.status || '');
  }

  rowKey(row: WarehousingShipmentWorkRow): string {
    return `${row.documentType}:${row.documentId}`;
  }

  isSelected(row: WarehousingShipmentWorkRow): boolean { return this.selectedKeys.has(this.rowKey(row)); }

  toggleRow(row: WarehousingShipmentWorkRow, checked: boolean): void {
    if (!this.isOpen(row)) return;
    if (checked) this.selectedKeys.add(this.rowKey(row));
    else this.selectedKeys.delete(this.rowKey(row));
    this.suggestNextBulkAction();
  }

  selectedRows(): WarehousingShipmentWorkRow[] {
    return this.visibleRows().filter(row => this.isSelected(row));
  }

  canReserveRow(row: WarehousingShipmentWorkRow): boolean {
    return (row.status || '').toUpperCase() === 'PLANNED' ||
      (row.documentType === 'DISPATCH' && (row.status || '').toUpperCase() === 'DRAFT');
  }

  canChooseSupplyRow(row: WarehousingShipmentWorkRow): boolean {
    return (this.canReserveRow(row) || (row.status || '').toUpperCase() === 'RESERVED')
      && Number(row.quantityReserved || 0) < Number(row.quantityRequested || 0);
  }

  supplyQueryParams(): Record<string, string | number | null> {
    return { ...this.workQueryParams(), reserveSupply: 1 };
  }

  selectMatchingGroup(): void {
    const anchor = this.selectedRows()[0];
    if (!anchor) return;
    const warehouse = this.bulkAction === 'TRANSFER_RECEIVE' ? anchor.destinationWarehouseId : anchor.sourceWarehouseId;
    for (const row of this.visibleRows()) {
      if (this.isOpen(row) && row.documentType === anchor.documentType &&
          (this.bulkAction !== 'RESERVE' || this.canReserveRow(row)) &&
          (this.bulkAction === 'TRANSFER_RECEIVE' ? row.destinationWarehouseId : row.sourceWarehouseId) === warehouse) {
        this.selectedKeys.add(this.rowKey(row));
      }
    }
    this.suggestNextBulkAction();
  }

  private suggestNextBulkAction(): void {
    const selected = this.selectedRows();
    if (!selected.length) return;
    if (this.workDirection === 'INBOUND') { this.bulkAction = 'TRANSFER_RECEIVE'; return; }
    if (selected.some(row => this.canReserveRow(row))) {
      this.bulkAction = 'RESERVE';
      return;
    }
    const rank = (status?: string): number => ({ PICKED: 1, STAGED: 2, LOADED: 3 } as Record<string, number>)[(status || '').toUpperCase()] || 0;
    const next = Math.min(...selected.map(row => rank(row.status)));
    this.bulkAction = next === 0 ? 'PICK' : next === 1 ? 'STAGE' : next === 2 ? 'LOAD'
      : selected[0].documentType === 'TRANSFER' ? 'TRANSFER_DISPATCH' : 'DISPATCH_EXIT';
  }

  clearRowSelection(): void { this.selectedKeys.clear(); }

  bulkActions(): Array<{ label: string; value: WarehousingBulkAction }> {
    const selected = this.selectedRows();
    const type = selected[0]?.documentType;
    const common: Array<{ label: string; value: WarehousingBulkAction }> = [
      ...(this.workDirection === 'OUTBOUND' && selected.some(row => this.canReserveRow(row))
        ? [{ label: 'Reserve Stock', value: 'RESERVE' as WarehousingBulkAction }] : []),
      { label: 'Confirm Pick', value: 'PICK' }, { label: 'Confirm Stage', value: 'STAGE' },
      { label: 'Confirm Load', value: 'LOAD' }
    ];
    return type === 'TRANSFER' ? [...common,
      { label: 'Dispatch Transfer', value: 'TRANSFER_DISPATCH' },
      { label: 'Receive Transfer', value: 'TRANSFER_RECEIVE' }]
      : type === 'DISPATCH' ? [...common, { label: 'Confirm Dispatch Exit', value: 'DISPATCH_EXIT' }] : [];
  }

  bulkActionLabel(): string {
    const labels: Record<WarehousingBulkAction, string> = {
      RESERVE: 'Reserve stock', PICK: 'Confirm pick', STAGE: 'Confirm stage', LOAD: 'Confirm load',
      TRANSFER_DISPATCH: 'Dispatch transfer', TRANSFER_RECEIVE: 'Receive transfer',
      DISPATCH_EXIT: 'Confirm dispatch exit'
    };
    return labels[this.bulkPreview?.action || this.bulkAction];
  }

  receiptZones(): WarehousingMasterData[] {
    const warehouseId = this.selectedRows()[0]?.destinationWarehouseId;
    return this.zones.filter(zone => zone.parentId === warehouseId);
  }

  previewBulk(): void {
    const rows = this.selectedRows();
    if (!rows.length || !this.work) return;
    this.isBulkBusy = true;
    this.warehousingService.previewShipmentBulkWork({
      shipmentNumber: this.work.shipmentNumber,
      deliveryNumber: this.work.selectedDeliveryNumber,
      action: this.bulkAction,
      documents: rows.map(row => ({ documentType: row.documentType as 'TRANSFER' | 'DISPATCH', documentId: row.documentId! })),
      destinationZoneId: this.bulkAction === 'TRANSFER_RECEIVE' ? this.selectedReceiptZoneId : null
    }).subscribe({
      next: response => {
        this.bulkPreview = response.data;
        const blockers = new Map<string, { reason: string; count: number;
          blockingDocument?: WarehousingBulkBlockingDocument | null }>();
        for (const row of this.bulkPreview?.rows || []) {
          if (!row.blockedReason) continue;
          const key = row.blockingDocument
            ? `${row.blockingDocument.documentType}:${row.blockingDocument.documentId}`
            : `reason:${row.blockedReason}`;
          const current = blockers.get(key);
          if (current) current.count++;
          else blockers.set(key, { reason: row.blockedReason, count: 1,
            blockingDocument: row.blockingDocument });
        }
        this.bulkBlockers = Array.from(blockers.values());
        this.bulkIdempotencyKey = crypto.randomUUID();
        this.bulkCorrelationId = crypto.randomUUID();
        this.bulkReason = '';
        this.bulkDialogVisible = true;
        this.isBulkBusy = false;
      },
      error: error => {
        this.isBulkBusy = false;
        this.messageService.add({ severity: 'error', summary: 'Preview Failed',
          detail: error?.error?.message || 'Unable to preview selected work.' });
      }
    });
  }

  commitBulk(): void {
    if (!this.bulkPreview || this.bulkPreview.blockedCount || !this.bulkReason.trim() || this.isBulkBusy) return;
    this.isBulkBusy = true;
    this.warehousingService.commitShipmentBulkWork({ previewToken: this.bulkPreview.token,
      reason: this.bulkReason.trim(), idempotencyKey: this.bulkIdempotencyKey,
      correlationId: this.bulkCorrelationId })
      .subscribe({
        next: response => {
          this.isBulkBusy = false;
          if (response.data?.status !== 'COMPLETED') {
            this.messageService.add({ severity: 'error', summary: 'Bulk Work Rejected',
              detail: response.data?.failure || 'Review the work and create a fresh preview.' });
            this.bulkDialogVisible = false;
            this.bulkPreview = undefined;
            this.search(false);
            return;
          }
          this.bulkDialogVisible = false;
          this.bulkPreview = undefined;
          this.messageService.add({ severity: 'success', summary: 'Bulk Work Recorded',
            detail: `${response.data?.documentCount || 0} documents and ${response.data?.unitCount || 0} units updated.` });
          this.search(false);
        },
        error: error => {
          this.isBulkBusy = false;
          this.bulkDialogVisible = false;
          this.bulkPreview = undefined;
          this.messageService.add({ severity: 'error', summary: 'Bulk Work Rejected',
            detail: error?.error?.message || 'Work changed; refresh and preview again.' });
          this.search(false);
        }
      });
  }

  showHistory(row: WarehousingShipmentWorkRow): void {
    if (!row.documentId || !row.documentType) return;
    this.historyTitle = `Work history: ${row.documentNumber || row.documentId}`;
    this.historyRows = [];
    this.historyDialogVisible = true;
    this.warehousingService.getShipmentWorkHistory(row.documentType as 'TRANSFER' | 'DISPATCH', row.documentId)
      .subscribe({ next: response => this.historyRows = response.data || [],
        error: error => this.messageService.add({ severity: 'error', summary: 'History Failed',
          detail: error?.error?.message || 'Unable to load work history.' }) });
  }

  documentIcon(row: WarehousingShipmentWorkRow): string {
    return row.documentType === 'TRANSFER' ? 'pi pi-sync' : 'pi pi-send';
  }

  flowLabel(row: WarehousingShipmentWorkRow): string {
    if (row.documentType === 'TRANSFER') {
      return `${row.sourceWarehouseLabel || 'Source'} -> ${row.destinationWarehouseLabel || 'Destination'}`;
    }
    return row.sourceWarehouseLabel || 'Dispatch warehouse';
  }

  detailLink(row: WarehousingShipmentWorkRow): string[] {
    const base = row.documentType === 'TRANSFER' ? '/warehousing/transfers/view' : '/warehousing/dispatch/view';
    return [base, String(row.documentId || '')];
  }

  blockingDocumentLink(document: WarehousingBulkBlockingDocument): string[] {
    const base = document.documentType === 'TRANSFER' ? '/warehousing/transfers/view' : '/warehousing/dispatch/view';
    return [base, String(document.documentId)];
  }

  scanLink(row: WarehousingShipmentWorkRow): string[] {
    const base = row.documentType === 'TRANSFER'
      ? '/warehousing/transfers/view'
      : '/warehousing/dispatch/view';
    return [base, String(row.documentId || ''), 'pick-scan'];
  }

  quantitySummary(row: WarehousingShipmentWorkRow): string {
    const parts = [
      `Req ${this.formatQty(row.quantityRequested)}`,
      `Res ${this.formatQty(row.quantityReserved)}`,
      `Out ${this.formatQty(row.quantityOutbound)}`
    ];
    if (row.documentType === 'TRANSFER') {
      parts.push(`In ${this.formatQty(row.quantityReceived)}`);
    }
    return parts.join(' | ');
  }

  workLineLabel(line: WarehousingShipmentWorkLine): string {
    const label = this.itemLabel(line, `Item ID ${line.itemId ?? 'not recorded'}`);
    return line.itemNumber || line.itemName || itemSizeLabel({ ...line, ...line.dimensions })
      ? label : `${label} | Size not recorded`;
  }

  formatQty(value?: number | null): string {
    return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
  }

  attachmentScopeLabel(file: WarehousingShipmentWorkAttachment): string {
    if (file.scope === 'DELIVERY_STOP') {
      return `Delivery stop${file.stopId ? ' #' + file.stopId : ''}`;
    }
    if (file.scope === 'DELIVERY') {
      return file.deliveryNumber ? `Delivery ${file.deliveryNumber}` : 'Delivery';
    }
    return 'Shipment';
  }

  attachmentSubtitle(file: WarehousingShipmentWorkAttachment): string {
    return [
      file.fileType || 'Attachment',
      this.fileSizeLabel(file.fileSize)
    ].filter(Boolean).join(' | ');
  }

  attachmentScopeIcon(file: WarehousingShipmentWorkAttachment): string {
    return file.scope === 'DELIVERY_STOP' ? 'pi pi-map-marker'
      : file.scope === 'DELIVERY' ? 'pi pi-truck' : 'pi pi-box';
  }

  private fileSizeLabel(size?: number | null): string | null {
    if (!size) return null;
    const bytes = Number(size);
    if (bytes < 1024) return `${bytes.toFixed(0)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  statusSeverity = statusSeverity;
}
